import { useTranslation } from "react-i18next";
import { useContext, useEffect, useState } from "react";
import Drug from "./Drug";
import Intakes from "./Intakes";
import DragNdrop from "./DragNDrop";
import InitContext from "../../pages/InitContext";
import useMmcService from "../../../services/MmcService";
import ErrorMessage from "../../errorMessage/ErrorMessage";
import Spinner from "../../spinner/Spinner";
import ReactDOM from "react-dom";
import { Consts } from "../../pages/consts";
import DrugRow from "./DrugRow";
import DrugInsertTypeRadioGroup from "./DrugInsertTypeRadioGroup";
import i18n from "i18next";
import {hospitalLanguages} from "../../../resources/json/jsons";

const Drugs = ({
  patDrugs,
  setPatDrugs,
  setQrFiles,
  openIntakeDialog,
  setOpenIntakeDialog,
}) => {
  const { t } = useTranslation();
  const [drugElems, setDrugElems] = useState([]);
  const [openDrugRow, setOpenDrugRow] = useState(false);
  const [allProductNames, setAllProductNames] = useState([]); // drug ProductNames prepared for select elements with value and label keys
  const [allSubstances, setAllSubstances] = useState([]); // drug active Substances prepared for select elements with value and label keys
  const [selectedDrug, setSelectedDrug] = useState(null);
  const [enteringKind, setEnteringKind] = useState(Consts.MANUALLY); // possible values: UPLOAD or MANUALLY
  const [initIntakes, setInitIntakes] = useState(false);
  const context = useContext(InitContext);

  const {
    loading,
    error,
    clearError,
    getDrugProductsJson,
    getDrugSubstancesJson,
    saveDbDrug,
    deleteDbDrug,
    getDbDrugs,
  } = useMmcService();

  const language = hospitalLanguages[context?.studyBranch];
  let messageText;

  useEffect(() => {
    buildDrugElems(patDrugs);
  }, [patDrugs]);

  useEffect(() => {
    clearError();

    getDrugProductsJson(context?.token, language)
      .then((data) => setAllProductNames(data))
      .catch((message) => (messageText = message));
  }, []);

  useEffect(() => {
    clearError();

    getDrugSubstancesJson(context?.token, language)
      .then((data) => setAllSubstances(data))
      .catch((message) => (messageText = message));
  }, [i18n.language]);

  const handleOpenIntakeDialog = (drug, init) => {
    if (drug) {
      setSelectedDrug(drug);
    }

    setInitIntakes(init);
    setOpenIntakeDialog(true);
  };

  const deleteDrug = (drugToDelete) => {
    clearError();

    const newDrugs = patDrugs?.filter((drug) => {
      return drugToDelete?.id
        ? drug?.id !== drugToDelete?.id
        : drug?.atc !== drugToDelete?.atc;
    });

    if (drugToDelete?.id) {
      deleteDbDrug(context?.token, drugToDelete?.id)
        .then( setPatDrugs(newDrugs))
        .catch((message) => (messageText = message));
    } else {
      setPatDrugs(newDrugs);
    }
  };

  const buildDrugElems = (drugs) => {
    const hasCompletedIntakes = (drug) => {
      if (drug?.intakes?.length > 0) {
        const intakesWithDosages = drug?.intakes?.filter((intake) => !isNaN(intake?.dosage)
          && intake?.dosage > 0);

        return intakesWithDosages?.length > 0;
      }

      return false;
    };

    const getDrugPriority = (drug) => {
      const needsSpecification = !hasCompletedIntakes(drug) && drug?.intakeType !== Consts.ON_DEMAND;
      return needsSpecification ? 0 : 1;
    };

    if (drugs?.length > 0) {
      setDrugElems(
        [...drugs]
          .map((drug, i) => ({ drug, originalIndex: i }))
          .sort((a, b) => {
            const priorityDiff = getDrugPriority(a.drug) - getDrugPriority(b.drug);

            return priorityDiff !== 0 ? priorityDiff : a.originalIndex - b.originalIndex;
          })
          .map(({ drug, originalIndex }) => {
          return (
            <DrugRow
              key={`drug${originalIndex}`}
              drug={drug}
              handleOpenIntakeDialog={handleOpenIntakeDialog}
              onDeleteDrug={deleteDrug}
            />
          );
        }),
      );
    } else {
      setDrugElems([]);
    }
  }

  const handleAddDrug = (drug) => {
    setPatDrugs(patDrugs?.length > 0 ? [...patDrugs, drug] : [drug]);
    setOpenDrugRow(false);
  };

  const saveDrug = (drug) => {
    saveDbDrug(context?.token, drug, context?.patient?.id).then(
      () => {
        clearError();

        getDbDrugs(context?.token, context?.patient?.id, false, true)
          .then((receivedDrugs) => {
            const notYetStoredDrugs = patDrugs?.filter(d => !d?.id && d?.productName !== drug?.productName);
            const savedAndNotYetStoredDrugs = [...receivedDrugs, ...notYetStoredDrugs];
            setSelectedDrug(null);
            setPatDrugs(savedAndNotYetStoredDrugs);
            setOpenIntakeDialog(false);
          });
      });
  };

  const handleEnteringKind = (event) => {
    setEnteringKind(event.target.value);
  };

  const handleAddDrugClick = () => {
    setSelectedDrug(null);
    setOpenDrugRow(true);
  };

  const errorMessage = error && <ErrorMessage message={messageText} />;
  const spinner = loading && <Spinner />;

  return (
    <div className="w3-container patient-init-drugs">
      {openIntakeDialog &&
        ReactDOM.createPortal(
          <Intakes
            onSaveDrug={saveDrug}
            selectedDrug={selectedDrug}
            setSelectedDrug={setSelectedDrug}
            initIntakes={initIntakes}
            showDialog={(value) => setOpenIntakeDialog(value)}
          />,
          document.getElementById("patient-init"),
        )}
      {errorMessage}
      {spinner}
      {(!patDrugs || patDrugs?.length === 0) && (
        <div className="w3-container radiogroup-centered patient-init-drugs__insert-type">
          <DrugInsertTypeRadioGroup
            enteringKind={enteringKind}
            handleEnteringKind={handleEnteringKind}
          />
        </div>
      )}
      {enteringKind === Consts.UPLOAD && (
        <div className="patient-init-drugs__upload-panel">
          <DragNdrop
            onFilesSelected={setQrFiles}
            accept=".pdf,.docx,.pptx,.txt,.xlsx"
          >
            <p>
              {t("drag_and_drop_medication_plan")}
              {". "}
              <b>({t("only_for_patients_located_in_germany")})</b>
            </p>
          </DragNdrop>
        </div>
      )}
      {enteringKind === Consts.MANUALLY && (
        <div className="patient-init-drugs__manual-panel">
          {drugElems?.length > 0 && (
            <div className="w3-container w3-padding-small form-content-scroll patient-init-drugs__list" style={{textAlign: "start"}}>
              <div className="patient-init-drugs__header" aria-hidden="true">
                <div className="patient-init-drugs__header-cell patient-init-drugs__header-cell--product">
                  <label>{t("drug_cap")}</label>
                </div>
                <div className="patient-init-drugs__header-cell patient-init-drugs__header-cell--substance">
                  <label>{t("active_substance")}</label>
                </div>
                <div className="patient-init-drugs__header-cell patient-init-drugs__header-cell--atc">
                  <label>{t("atc")}</label>
                </div>
                <div className="patient-init-drugs__header-cell patient-init-drugs__header-cell--date">
                  <label>{t("start_date")}</label>
                </div>
                <div className="patient-init-drugs__header-cell patient-init-drugs__header-cell--actions">
                  <label>{t("intake_plan")}</label>
                </div>
                <div className="patient-init-drugs__header-cell patient-init-drugs__header-cell--delete" />
              </div>
              <div className="patient-init-drugs__rows">{drugElems}</div>
            </div>
          )}
          <>
            {openDrugRow &&
              ReactDOM.createPortal(
                <Drug
                  onHandleAddDrug={handleAddDrug}
                  setOpenDrugRow={setOpenDrugRow}
                  allProductNames={allProductNames}
                  allSubstances={allSubstances}
                  showDialog={openDrugRow}
                  patDrugs={patDrugs}
                  language={language}
                />,
                document.getElementById("patient-init"),
              )}
            <div className="w3-center w3-padding w3-margin-top add-medicine-actions patient-init-drugs__actions">
              <button className="w3-btn w3-round-xlarge w3-theme-d5" onClick={handleAddDrugClick}>
                + {t("add_medicine")}
              </button>
            </div>
          </>
        </div>
      )}
    </div>
  );
};

export default Drugs;
